import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import Markdown from 'components/markdown';
import useOnScreen from 'hooks/common/use-on-screen';
import RibbonText from 'modules/content-cms/components/ribbon-text';
import renderDocument from 'modules/content-cms/util/render-document';
import Link from 'next/link';
import React from 'react';
import { gtm, selectElement } from 'tracking';
import { Gtm } from 'tracking/gtm';
import Button from '../../../../components/button';
import { IHeaderWithPromoFields } from '../../../../global';
import Image from '../components/image';

const HeaderWithPromo: React.FC<IHeaderWithPromoFields & { id: string }> = ({
  disrupterBackgroundColor,
  disrupterText,
  id,
  layout,
  promoBackgroundColor,
  promoButtonLayout,
  promoButtonText,
  promoHeaderText,
  promoHeaderTitle,
  promoImage,
  promoImageMobile,
  promoLink,
  promoTextColor,
  promoTitleColor,
  richTitle,
  showOnlyOnBranch,
}) => {
  const $promoHeader = React.useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen($promoHeader);

  // handles getting value from rich text document node
  const promoTitleValue = React.useMemo(() => {
    return (
      promoHeaderTitle && (promoHeaderTitle.content[0].content[0] as any).value
    );
  }, [promoHeaderTitle]);

  // gtm fires for impression if promo header enters the viewport
  React.useEffect(() => {
    let timeout: null | NodeJS.Timeout = null;
    if (onScreen === true) {
      timeout = setTimeout(() => {
        gtm({
          group: Gtm.GroupName.PromoHeader,
          name: Gtm.CommonEventName.View,
          data: {
            title: promoTitleValue,
          },
        });
      }, 500);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [onScreen, promoTitleValue]);

  const getDisrupterColor = (): string => {
    switch (disrupterBackgroundColor) {
      case 'Brand-Blue':
        return '#1A335B';
      case 'Bermuda-Grau':
        return '#798ca4';
      case 'Red-Sale':
        return '#FF5768';
      case 'Blue-Neu':
        return '#5500FF';
      case 'Limited-Green':
        return '#3ECBAF';
      default:
        return '#F7EB60';
    }
  };

  const trackingId = ['Contentful', id];

  if (showOnlyOnBranch === 'control') return null;

  const renderPromoBanner = (): JSX.Element => {
    return (
      <ImpressionTrackerNew element={trackingId}>
        <section
          data-test="promo-banner-section"
          data-variant="promo-banner"
          className="container px-0 py-6 lg:relative lg:py-10"
        >
          <Link
            href={promoLink}
            onClick={() => gtm(selectElement({ element: trackingId }))}
            className="border-grey-50 flex flex-col rounded-xl border shadow-lg lg:h-60"
          >
            <div className="flex flex-col lg:flex-row">
              <div
                className="relative -mx-4 hidden h-60 lg:mx-0 lg:block lg:w-1/2"
                style={{ fontSize: 0 }}
              >
                <Image
                  className="lg:rounded-l-xl"
                  data-test="promo-header-dt"
                  layout={'fill'}
                  objectFit={'cover'}
                  {...promoImage}
                />
              </div>
              <div className="lg:hidden">
                <Image
                  className="rounded-t-xl"
                  data-test="promo-header-mb"
                  layout={'responsive'}
                  {...promoImageMobile}
                />
              </div>
              <div className="flex flex-col items-center justify-between p-4 lg:w-1/2 lg:px-10 lg:py-6">
                <div>
                  <div className="text-primary-main text-2xl font-black">
                    <Markdown>{renderDocument(promoHeaderTitle)}</Markdown>
                  </div>
                  <Markdown>{renderDocument(promoHeaderText)}</Markdown>
                </div>
                <Button
                  data-test="promo-banner-button"
                  layout={'brand'}
                  className="mt-4 h-11 w-2/3 rounded-lg! lg:mt-auto lg:h-10 lg:w-80 lg:self-start"
                >
                  {promoButtonText}
                </Button>
              </div>
            </div>
          </Link>
        </section>
      </ImpressionTrackerNew>
    );
  };

  if (layout === 'Promo Banner') {
    return renderPromoBanner();
  }

  return (
    <ImpressionTrackerNew element={trackingId}>
      <section
        data-test="promo-header-section"
        data-variant="new-header"
        className="container lg:relative lg:py-10"
        ref={$promoHeader}
      >
        <div
          className="bg-brand-beige -mx-4 lg:mx-0 lg:h-105 lg:rounded-xl lg:shadow-lg"
          style={{
            backgroundColor: promoBackgroundColor,
          }}
        >
          <div className="px-4 lg:px-0">
            <Link href={promoLink} passHref>
              <a
                className="relative flex flex-col lg:flex-row"
                onClick={() => {
                  gtm(selectElement({ element: trackingId }), {
                    group: Gtm.GroupName.PromoHeader,
                    name: Gtm.CommonEventName.Click,
                    data: {
                      title: promoTitleValue,
                    },
                  });
                }}
              >
                <div
                  className="relative -mx-4 lg:mx-0 lg:h-105 lg:w-1/2"
                  style={{ fontSize: 0 }}
                >
                  <Image
                    className="lg:rounded-l-xl"
                    data-test="promo-header-dt"
                    layout={'fill'}
                    objectFit={'cover'}
                    {...promoImage}
                  />
                </div>
                <div className="-mx-4 lg:hidden">
                  <Image
                    data-test="promo-header-mb"
                    layout={'responsive'}
                    {...promoImageMobile}
                  />
                </div>
                {disrupterText && (
                  <div
                    style={{
                      backgroundColor: getDisrupterColor(),
                    }}
                    className="text-other-white absolute top-0 left-0 mb-3 -ml-4 flex inline-block h-12 w-24 justify-center rounded-br-lg px-2 pt-2 pb-1 text-3xl font-black shadow-sm lg:ml-0 lg:flex lg:rounded-tl-lg"
                  >
                    {disrupterText}
                  </div>
                )}
                <div className="w-full pt-4 pb-4 lg:flex lg:w-1/2 lg:flex-col lg:justify-center lg:rounded-r-lg lg:p-10 lg:pt-0 lg:pt-10">
                  <div
                    className="text-other-white mb-2 text-2xl leading-tight font-normal lg:pb-1 lg:text-3xl"
                    style={{
                      color: promoTitleColor,
                    }}
                  >
                    {richTitle ? (
                      <RibbonText text={richTitle} />
                    ) : promoHeaderTitle ? (
                      <Markdown>{renderDocument(promoHeaderTitle)}</Markdown>
                    ) : null}
                  </div>
                  <div
                    className="leading-wide text-other-white pb-4 text-base font-normal lg:text-lg"
                    style={{
                      color: promoTextColor,
                    }}
                  >
                    <Markdown>{renderDocument(promoHeaderText)}</Markdown>
                  </div>
                  <Button
                    data-test="promo-header-button"
                    layout={promoButtonLayout ?? 'brand'}
                    className="text-other-white w-full rounded-lg! border-0 lg:w-80 lg:border-0"
                  >
                    {promoButtonText}
                  </Button>
                </div>
              </a>
            </Link>
          </div>
        </div>
      </section>
    </ImpressionTrackerNew>
  );
};

export default HeaderWithPromo;
